import React from 'react';
import Toggle from '../Toggle';
import { SizeGuideTable, SizeGuideTableRow, SystemOfMeasurements } from './sizeChartTypes';
import { notNil } from 'src/scripts/utils/typescriptUtils';

const getValuesAndUnit = (row: SizeGuideTableRow, systemOfMeasurements: SystemOfMeasurements) => {
  const defaultValues = row?.values?.default ?? [];
  const cmValues = row?.values?.cm ?? [];
  const hasCmValues = cmValues.length > 0;
  const useCm = hasCmValues && systemOfMeasurements !== 'imperial';
  const values = useCm ? cmValues : defaultValues;
  const defaultUnit = row.defaultUnit === 'none' ? '' : row.defaultUnit;
  const unit = useCm ? ' (cm)' : defaultUnit ? ` (${defaultUnit})` : '';
  return { values, unit };
};

interface GenerateTableHeadersProps {
  table: SizeGuideTable;
  systemOfMeasurements: SystemOfMeasurements;
  toggleCallback?: (position: string) => void;
}

export const generateTableHeaders = ({
  table,
  systemOfMeasurements,
  toggleCallback,
}: GenerateTableHeadersProps): JSX.Element[][] => {
  const { headers = [], columnsSize, enableToggle } = table;
  return headers.map((row, rowIndex) => {
    const cells: JSX.Element[] = [];

    const { values, unit } = getValuesAndUnit(row, systemOfMeasurements);

    // First cell: toggle or title
    if (enableToggle && rowIndex === 0) {
      cells.push(
        <th key="toggle" className="toggle-cell">
          <Toggle
            label="Switch system of measurements"
            defaultPosition={systemOfMeasurements === 'metric' ? 'left' : 'right'}
            rightLabel="in"
            leftLabel="cm"
            onChange={toggleCallback}
          />
        </th>
      );
    } else {
      cells.push(
        <th key={`title-${rowIndex}`} tabIndex={0}>
          {row.title}
          {unit}
        </th>
      );
    }

    for (let colIndex = 0; colIndex < columnsSize; colIndex++) {
      const columnValue = values[colIndex]?.value || '';
      cells.push(
        <th key={`header-${rowIndex}-${colIndex}`} tabIndex={0} className="header-cell">
          {columnValue}
        </th>
      );
    }

    return cells;
  });
};

interface GenerateTableRowsProps {
  table: SizeGuideTable;
  currentCountry: string;
  systemOfMeasurements: SystemOfMeasurements;
}

export const generateTableRows = ({
  table,
  currentCountry,
  systemOfMeasurements,
}: GenerateTableRowsProps): Record<string, string>[] => {
  const { rows = [], columnsSize } = table;

  return rows
    .map((row) => {
      const { enableLocalize, countryCode } = row;

      if (enableLocalize && countryCode !== currentCountry) {
        return null;
      }

      const { values, unit } = getValuesAndUnit(row, systemOfMeasurements);

      const result: Record<string, string> = {};
      result.name = row.title ? row.title + unit : '';

      for (let i = 0; i < columnsSize; i++) {
        result[i.toString()] = values[i]?.value || '';
      }

      return result;
    })
    .filter(notNil);
};
